/**
 * Interactive Rooms - Hotspot Slider
 * Features:
 * - 1.5 slide preview with loop
 * - Interactive hotspots with product cards
 * - Auto-detect card position (left/right)
 * - Color variant switching
 * - Judge.me integration
 * - Touch/click support for mobile
 */

class InteractiveRooms {
  constructor(container) {
    this.container = container;
    this.swiper = null;
    this.activeHotspot = null;
    this.init();
  }

  init() {
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.initSwiper();
    this.setupHotspots();
    this.setupVariantSwitching();
    this.initJudgeMe();
  }

  /**
   * Initialize Swiper Slider
   */
  initSwiper() {
    const swiperElement = this.container.querySelector('.interactive-rooms-swiper');
    if (!swiperElement) return;

    // Get settings from data attributes
    const autoplay = swiperElement.dataset.autoplay === 'true';
    const autoplayDelay = parseInt(swiperElement.dataset.autoplayDelay) || 6000;
    const slidesDesktop = parseFloat(swiperElement.dataset.slidesDesktop) || 1.5;
    const slidesTablet = parseFloat(swiperElement.dataset.slidesTablet) || 1.3;
    const slidesMobile = parseFloat(swiperElement.dataset.slidesMobile) || 1.2;
    const spaceBetween = parseInt(swiperElement.dataset.spaceBetween) || 24;
    const loop = swiperElement.dataset.loop === 'true';

    // Initialize Swiper
    this.swiper = new Swiper(swiperElement, {
      // Core settings
      slidesPerView: slidesMobile,
      spaceBetween: 16,
      loop: loop,
      centeredSlides: false,
      grabCursor: true,
      watchOverflow: true,
      slidesPerGroup: 1,
      speed: 600,

      // Autoplay
      autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,

      // Navigation
      navigation: {
        nextEl: this.container.querySelector('.interactive-rooms-button--next'),
        prevEl: this.container.querySelector('.interactive-rooms-button--prev'),
      },

      // Pagination
      pagination: {
        el: this.container.querySelector('.interactive-rooms-pagination'),
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 3,
      },

      // Responsive breakpoints
      breakpoints: {
        // Mobile (>= 480px)
        480: {
          slidesPerView: slidesMobile,
          spaceBetween: 16,
        },
        // Tablet (>= 750px)
        750: {
          slidesPerView: slidesTablet,
          spaceBetween: 20,
        },
        // Desktop (>= 990px)
        990: {
          slidesPerView: slidesTablet,
          spaceBetween: spaceBetween,
        },
        // Large Desktop (>= 1200px)
        1200: {
          slidesPerView: slidesDesktop,
          spaceBetween: spaceBetween,
        },
      },

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous room',
        nextSlideMessage: 'Next room',
      },

      // Keyboard control
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // Events
      on: {
        init: function() {
          swiperElement.classList.add('swiper-initialized');
        },
        slideChange: () => {
          // Close any open hotspots when sliding
          this.closeAllHotspots();
        },
      },
    });

    // Pause autoplay when hotspot is active
    if (autoplay) {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.swiper.autoplay.stop();
        } else if (!this.activeHotspot) {
          this.swiper.autoplay.start();
        }
      });
    }
  }

  /**
   * Setup Hotspot Interactions
   */
  setupHotspots() {
    const hotspots = this.container.querySelectorAll('.hotspot-trigger');

    hotspots.forEach(hotspot => {
      const button = hotspot.querySelector('.hotspot-button');
      const card = hotspot.querySelector('.hotspot-product-card');

      if (!button || !card) return;

      // Desktop: Hover to show
      if (window.innerWidth > 768) {
        hotspot.addEventListener('mouseenter', () => {
          this.showHotspotCard(hotspot);
        });

        hotspot.addEventListener('mouseleave', (e) => {
          // Don't hide if moving to card
          if (!hotspot.contains(e.relatedTarget)) {
            this.hideHotspotCard(hotspot);
          }
        });
      }

      // Mobile + Desktop: Click to toggle
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleHotspotCard(hotspot);
      });
    });

    // Close hotspots when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.hotspot-trigger')) {
        this.closeAllHotspots();
      }
    });
  }

  /**
   * Show hotspot card (positioned above hotspot via CSS)
   */
  showHotspotCard(hotspot) {
    // Close other hotspots
    this.closeAllHotspots();

    const card = hotspot.querySelector('.hotspot-product-card');
    if (!card) return;

    // Show card
    card.classList.add('is-visible');
    hotspot.classList.add('is-active');
    this.activeHotspot = hotspot;

    // Pause autoplay
    if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) {
      this.swiper.autoplay.stop();
    }
  }

  /**
   * Hide hotspot card
   */
  hideHotspotCard(hotspot) {
    const card = hotspot.querySelector('.hotspot-product-card');
    if (!card) return;

    card.classList.remove('is-visible');
    hotspot.classList.remove('is-active');

    if (this.activeHotspot === hotspot) {
      this.activeHotspot = null;

      // Resume autoplay
      const autoplay = this.container.querySelector('.interactive-rooms-swiper').dataset.autoplay === 'true';
      if (this.swiper && autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  /**
   * Toggle hotspot card (for mobile)
   */
  toggleHotspotCard(hotspot) {
    if (hotspot.classList.contains('is-active')) {
      this.hideHotspotCard(hotspot);
    } else {
      this.showHotspotCard(hotspot);
    }
  }

  /**
   * Close all hotspot cards
   */
  closeAllHotspots() {
    const activeHotspots = this.container.querySelectorAll('.hotspot-trigger.is-active');
    activeHotspots.forEach(hotspot => {
      this.hideHotspotCard(hotspot);
    });
  }

  /**
   * Card positioning is now handled purely by CSS (above hotspot)
   * This method is kept for potential future use
   */
  positionCard(hotspot, card) {
    // Card is positioned above hotspot via CSS
    // No dynamic positioning needed
  }

  /**
   * Variant Switching - Color swatches
   */
  setupVariantSwitching() {
    const cards = this.container.querySelectorAll('.hotspot-product-card');

    cards.forEach(card => {
      const swatches = card.querySelectorAll('.hotspot-color-swatch input[type="radio"]');
      const thumbnail = card.querySelector('.hotspot-product-thumbnail');
      const priceElement = card.querySelector('[data-product-price] .hotspot-price-regular');
      const productId = card.querySelector('[data-product-id]')?.dataset.productId;

      swatches.forEach(swatch => {
        swatch.addEventListener('change', (e) => {
          const variantId = e.target.dataset.variantId;
          
          // Update active swatch styling
          swatches.forEach(s => {
            s.closest('.hotspot-color-swatch').classList.remove('active');
          });
          e.target.closest('.hotspot-color-swatch').classList.add('active');

          // Update variant data
          if (window.interactiveRoomsVariants && window.interactiveRoomsVariants[productId]) {
            const variantData = window.interactiveRoomsVariants[productId][variantId];
            
            if (variantData) {
              // Update image with fade effect
              if (variantData.image && thumbnail) {
                thumbnail.style.opacity = '0';
                setTimeout(() => {
                  thumbnail.src = variantData.image;
                  thumbnail.style.opacity = '1';
                }, 200);
              }

              // Update price
              if (variantData.price && priceElement) {
                priceElement.textContent = variantData.price;
              }
            }
          }
        });
      });

      // Set first swatch as active
      const firstSwatch = card.querySelector('.hotspot-color-swatch input[type="radio"]:checked');
      if (firstSwatch) {
        firstSwatch.closest('.hotspot-color-swatch').classList.add('active');
      }
    });
  }

  /**
   * Initialize Judge.me
   */
  initJudgeMe() {
    // Check if Judge.me is loaded
    if (typeof window.jdgm !== 'undefined') {
      window.jdgm.customBadges();
    } else {
      // Wait for Judge.me to load
      const checkJudgeMe = setInterval(() => {
        if (typeof window.jdgm !== 'undefined') {
          window.jdgm.customBadges();
          clearInterval(checkJudgeMe);
        }
      }, 500);

      // Stop checking after 10 seconds
      setTimeout(() => {
        clearInterval(checkJudgeMe);
      }, 10000);
    }
  }

  /**
   * Clean up on disconnect
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.interactive-rooms-section');
  containers.forEach(container => {
    new InteractiveRooms(container);
  });
});

// Reinitialize on Shopify section reload (Theme Editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.interactive-rooms-section');
    if (container) {
      new InteractiveRooms(container);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('.interactive-rooms-section');
    if (container && container._interactiveRooms) {
      container._interactiveRooms.destroy();
    }
  });
}

// Keyboard accessibility - ESC to close hotspots
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const activeHotspots = document.querySelectorAll('.hotspot-trigger.is-active');
    activeHotspots.forEach(hotspot => {
      hotspot.classList.remove('is-active');
      const card = hotspot.querySelector('.hotspot-product-card');
      if (card) {
        card.classList.remove('is-visible');
      }
    });
  }
});

// Handle window resize - reposition cards
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    // Reposition any visible cards
    const activeHotspots = document.querySelectorAll('.hotspot-trigger.is-active');
    activeHotspots.forEach(hotspot => {
      const card = hotspot.querySelector('.hotspot-product-card');
      if (card && card.classList.contains('is-visible')) {
        // Recalculate position
        const section = hotspot.closest('.interactive-rooms-section');
        if (section && section._interactiveRooms) {
          section._interactiveRooms.positionCard(hotspot, card);
        }
      }
    });
  }, 250);
});
